/*
 * A visualization component that draws the arrow-like shape of a single gene feature.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is pileup feature whose view coordinates and a width have already been computed elsewhere. The
 * VariantShape renders a left or right arrow depending of the genomic strand. The name of the gene feature is drawn
 * either inside or to the left of the arrow depending if the label fits into the shape.
 *
 */
import { type ReactElement } from "react";

function renderForwardShape(
  shapeId: string,
  height: number,
  width: number,
  shapeClasses: string,
  shapeStyle: any,
): ReactElement {
  const halfHeight: number = height / 2;

  const p1 = "0,0";
  const p2 = `0,${height}`;
  const p3 = `${width - halfHeight},${height}`;
  const p4 = `${width},${halfHeight}`;
  const p5 = `${width - halfHeight},0`;

  return <polygon id={shapeId} className={shapeClasses} points={`${p1} ${p2} ${p3} ${p4} ${p5}`} style={shapeStyle} />;
}

function renderBackwardShape(
  shapeId: string,
  height: number,
  width: number,
  shapeClasses: string,
  shapeStyle: any,
): ReactElement {
  const halfHeight: number = height / 2;

  const p1 = `${halfHeight},0`;
  const p2 = `0,${halfHeight}`;
  const p3 = `${halfHeight},${height}`;
  const p4 = `${width},${height}`;
  const p5 = `${width},0`;

  return <polygon id={shapeId} className={shapeClasses} points={`${p1} ${p2} ${p3} ${p4} ${p5}`} style={shapeStyle} />;
}

function renderCutOffShape(
  shapeId: string,
  height: number,
  width: number,
  shapeClasses: string,
  shapeStyle: any,
): ReactElement {
  // Adding 0.5 to make the shape look crisp, see https://stackoverflow.com/a/22167772
  return <rect id={shapeId} className={shapeClasses} width={width} height={height} style={shapeStyle} />;
}

function renderArrowShape(
  name: string,
  shapeId: string,
  height: number,
  width: number,
  textY: number,
  textDY: string,
  startCut: boolean,
  endCut: boolean,
  isForwardStrand: boolean,
  arrowFits: boolean,
  labelFits: boolean,
  hideLabel: boolean,
  shapeClasses: string,
  shapeStyle: any,
  textClasses: string,
  textStyle: any,
  groupClasses: string,
  translateFeature: string,
  onClick: (e: any) => void = null,
): ReactElement {
  // Create actual shape and label elements
  let shape: ReactElement = null;
  let label: ReactElement = null;

  const halfHeight: number = height / 2;

  if (arrowFits && isForwardStrand && !endCut) {
    shape = renderForwardShape(shapeId, height, width, shapeClasses, shapeStyle);

    if (!hideLabel) {
      label = (
        <text
          x={labelFits ? (width - halfHeight) / 2 : -2}
          y={textY}
          dy={textDY}
          className={textClasses}
          style={textStyle}
        >
          {name}
        </text>
      );
    }
  } else if (arrowFits && !isForwardStrand && !startCut) {
    shape = renderBackwardShape(shapeId, height, width, shapeClasses, shapeStyle);

    if (!hideLabel) {
      label = (
        <text
          x={labelFits ? halfHeight + (width - halfHeight) / 2 : -2}
          y={textY}
          dy={textDY}
          className={textClasses}
          style={textStyle}
        >
          {name}
        </text>
      );
    }
  } else {
    shape = renderCutOffShape(shapeId, height, width, shapeClasses, shapeStyle);

    if (!hideLabel) {
      label = (
        <text x={labelFits ? width / 2 : -2} y={textY} dy={textDY} className={textClasses} style={textStyle}>
          {name}
        </text>
      );
    }
  }

  return (
    <g key={shapeId} className={groupClasses} transform={translateFeature} onClick={onClick}>
      {shape}
      {label}
    </g>
  );
}

export { renderArrowShape };
